/**
 * Exclusive Elite family landing (mirrors `/elite-prime-family-landing` with Elite locked).
 *
 * Belongs here:
 * - Secondary already Elite → Active Elite dashboard
 * - Secondary free + family purchase → FamilyPurchaseBody
 * - Primary free / blocked → SellBody (Upgrade / blocked CTA)
 * Does not belong here: tabbed Elite+Prime family landing (`/elite-plans-family-landing`).
 */
import '../elitePlansTheme.css'
import {
  ELITE_PLANS_ELITE_TAB_ID,
  ElitePlansLandingProvider,
  useElitePlansLanding
} from '../ElitePlansLandingContext'
import { ElitePlansAnalyticsScreen } from '../constants/analyticsEvents'
import { ELITE_FAMILY_LANDING_STATUS_QUERY_KEY } from '../constants/queryKeys'
import {
  resolveIsFamilyPurchase,
  resolveIsFamilySecondaryElite
} from '../elitePlansMembership'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'
import { ElitePlansPageShell } from '../components/ElitePlansPageShell'
import { ElitePrimeDashboardHeader } from '../components/ElitePrimeDashboardHeader'
import { ElitePlansEliteMemberDashboard } from '../components/ElitePlansEliteMemberDashboard'
import { ElitePlansFamilyPurchaseBody } from '../components/ElitePlansFamilyPurchaseBody'
import { ElitePlansSellLandingChrome } from '../components/ElitePlansSellLandingChrome'
import { ElitePlansSellBody } from '../components/ElitePlansSellBody'
import { ElitePlansHeroStatusSkeleton } from '../components/hero/ElitePlansHeroStatusSkeleton'
import { NewEliteLandingAnalyticsScreen } from '../../new-elite-landing/analytics/newEliteLandingAnalytics'
import { useTrackElitePlansScreenLoadWhenReady } from '../hooks/useTrackElitePlansScreenLoadWhenReady'

export default function ElitePlansEliteFamilyLandingPage() {
  return (
    <ElitePlansLandingProvider
      analyticsScreen={ElitePlansAnalyticsScreen.EliteFamilyLanding}
      lockedPlanId={ELITE_PLANS_ELITE_TAB_ID}
      statusQueryKeyRoot={ELITE_FAMILY_LANDING_STATUS_QUERY_KEY}
    >
      <ElitePlansEliteFamilyLandingPageContent />
    </ElitePlansLandingProvider>
  )
}

function ElitePlansEliteFamilyLandingPageContent() {
  const { status, isStatusLoading } = useElitePlansLanding()
  useTrackElitePlansScreenLoadWhenReady(!isStatusLoading)

  // Wait for status — do not flash SellBody before FamilyPurchase / dashboard resolves
  if (isStatusLoading) {
    return (
      <ElitePlansPageShell isContentScrollOwner bottomInsetPx={24}>
        <ElitePrimeDashboardHeader accent='elite' showPlanTab={false} />
        <div className='px-5'>
          <ElitePlansHeroStatusSkeleton showTenure showPlusBanner />
        </div>
      </ElitePlansPageShell>
    )
  }

  // Secondary already has Elite → Active Elite dashboard (not purchase)
  if (resolveIsFamilySecondaryElite(status)) {
    return (
      <ElitePlansPageShell>
        <ElitePrimeDashboardHeader accent='elite' showPlanTab={false} />
        <ElitePlansEliteMemberDashboard
          statusQueryKeyRoot={ELITE_FAMILY_LANDING_STATUS_QUERY_KEY}
          analyticsScreen={NewEliteLandingAnalyticsScreen.FamilyDashboard}
        />
      </ElitePlansPageShell>
    )
  }

  // Free secondary → family Continue
  if (resolveIsFamilyPurchase(status)) {
    return (
      <EliteBuyCtaExtrasProvider status={status}>
        <ElitePlansPageShell isContentScrollOwner bottomInsetPx={24}>
          <ElitePrimeDashboardHeader accent='elite' showPlanTab={false} />
          <ElitePlansFamilyPurchaseBody />
        </ElitePlansPageShell>
      </EliteBuyCtaExtrasProvider>
    )
  }

  return (
    <ElitePlansSellLandingChrome>
      <ElitePrimeDashboardHeader accent='elite' showPlanTab={false} />
      <ElitePlansSellBody />
    </ElitePlansSellLandingChrome>
  )
}
